import type { SessionInfosResponse, UserDataResponse } from 'services/types';
import type { PixumEnvType } from 'utils';

type BuildUserExtensionArgs = {
  isAuthenticated: boolean;
  uuid: string | null;
  sessionInfos: SessionInfosResponse | null;
  userData: UserDataResponse | null;
};

/**
 * Extracts the first regex match group from a string (mirrors UserStoreInitializer.matchRegex).
 */
function matchRegex(str: string, regEx: RegExp): string | undefined {
  const group = str?.match(regEx);

  return group ? group[0] : undefined;
}

/**
 * Derives structured prefInfo from a raw pref string.
 *
 * Mirrors the transformPrefInfo logic in UserStoreInitializer so that
 * PIXUM_ENV.USER.prefInfo is populated before the client store
 * initializes — avoiding a blank prefInfo on first read.
 */
function computePrefInfo(pref: string): { campaignId?: string; pref?: string; prefFull?: string } {
  const campaignStr = matchRegex(pref, /c\d+/);
  const prefShort = matchRegex(pref, /cp[a-zA-Z]?_[a-zA-Z]*/);
  const campaignId = campaignStr ? String(parseInt(campaignStr.substring(1))) : undefined;

  return { campaignId, pref: prefShort, prefFull: pref };
}

/**
 * Builds PIXUM_ENV.USER from session infos and user data. Shared by the server (PixumEnvExtensionStreaming)
 * and the client (SessionClient) path so both emit the identical object.
 */
export function buildUserExtension({
  isAuthenticated,
  uuid,
  sessionInfos,
  userData,
}: BuildUserExtensionArgs): Partial<PixumEnvType['USER']> {
  // Prefer PSI-returned prefInfo, fall back to regex-derived value.
  const prefInfo = sessionInfos?.prefInfo ?? (sessionInfos?.pref ? computePrefInfo(sessionInfos.pref) : undefined);

  return {
    isAuthenticated,
    ...(sessionInfos && {
      pref: sessionInfos.pref ?? '',
      voucher: sessionInfos.voucher ?? '',
      ...(prefInfo && { prefInfo }),
    }),
    ...(userData && {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      birthday: userData.birthday,
      sex: userData.sex,
      ...(userData.isNewCustomerSignup !== undefined && { isNewCustomerSignup: userData.isNewCustomerSignup }),
    }),
    id: uuid ?? '',
  };
}
